import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { swayStyleFor } from "@/lib/swayStyle";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data.products);
      setLoading(false);
    });
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16" data-testid="shop-page">
      <SEO
        title="Shop All Scents"
        description="Browse the full Booty Breeze collection of small-batch adult (18+) novelty car air fresheners. Six hand-poured scents, $8.99 each, free US shipping over $10. Lasts 3–4 weeks. Ships discreetly from the USA."
        path="/shop"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Booty Breeze Air Fresheners",
          "itemListElement": products.slice(0, 12).map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "url": `https://bootybreeze.com/product/${p.id}`,
            "name": p.name,
          })),
        }}
      />
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <div>
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3">
            The Collection
          </div>
          <h1 className="font-display text-5xl sm:text-7xl text-white">Shop All</h1>
        </div>
        <p className="text-white/60 max-w-sm text-sm leading-relaxed">
          Every scent is small-batch, hand-poured, and lasts 3–4 weeks. Free US shipping over $10.
        </p>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading the good stuff…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, idx) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.5 }}
            >
              <Link
                to={`/product/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group block rounded-xl overflow-hidden hover:-translate-y-1 hover:drop-shadow-[0_0_30px_rgba(255,61,139,0.35)] transition-[transform,filter] duration-300"
              >
                <div className="relative aspect-square overflow-visible p-6 sm:p-8">
                  <img
                    src={resolveImage(p.image_url || p.image, 600, { removeBg: true })}
                    alt={p.name}
                    className="sway-hang w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_15px_30px_rgba(255,61,139,0.35)]"
                    style={swayStyleFor(p.id)}
                  />
                  {p.badge && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF3D8B] text-black font-bold">
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="px-2 pb-5">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <div className="font-display text-xl text-white group-hover:text-[#FF3D8B] transition-colors">
                      {p.name}
                    </div>
                    <div className="text-white font-bold text-sm">${p.price.toFixed(2)}</div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">{p.tagline}</p>
                  <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#A855F7] group-hover:gap-2.5 transition-all">
                    View scent <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
